import React from 'react';
import { Link } from 'react-router-dom';
import { 
  PaintBucket, 
  Home as HomeIcon, 
  Sparkles, 
  Hammer, 
  Droplets,
  DoorOpen,
  CheckCircle2,
  Phone,
  ClipboardList,
  Calendar,
  FileText,
  Star,
  MapPin,
  ArrowRight
} from 'lucide-react';
import { GlowingCard } from '../components/GlowingCard';

export const HomePage = () => {
  const services = [
    {
      id: 'interior-painting',
      icon: HomeIcon,
      title: 'Interior Painting',
      description: 'Transform your living spaces with expert color consultation and flawless finishes.',
      featured: true
    },
    {
      id: 'exterior-painting',
      icon: PaintBucket,
      title: 'Exterior Painting',
      description: 'Protect and beautify your home with durable, weather-resistant exterior coatings.'
    },
    {
      id: 'drywall-repair',
      icon: Hammer,
      title: 'Sheetrock / Drywall Repair',
      description: 'Seamless repairs for cracks, holes, and water damage before painting.'
    },
    {
      id: 'pressure-washing',
      icon: Droplets,
      title: 'Pressure Washing',
      description: 'Deep clean surfaces to ensure optimal paint adhesion and curb appeal.'
    },
    {
      id: 'trim-doors-cabinets',
      icon: DoorOpen,
      title: 'Trim, Doors & Cabinets',
      description: 'Detailed finishing work for all woodwork, trim, and cabinet refinishing.'
    },
    {
      id: 'minor-prep',
      icon: Sparkles,
      title: 'Minor Prep Work',
      description: 'Caulking, patching, sanding, and surface preparation for perfect results.'
    }
  ];

  const processSteps = [
    { id: 'step-contact', icon: Phone, title: 'Contact', description: 'Reach out for a free consultation', link: '/contact' },
    { id: 'step-consult', icon: ClipboardList, title: 'Consult', description: 'We assess your project needs', link: null },
    { id: 'step-proposal', icon: FileText, title: 'Proposal', description: 'Receive a detailed written estimate', link: null },
    { id: 'step-schedule', icon: Calendar, title: 'Schedule', description: 'Book your project at your convenience', link: '/free-strategy-call' }
  ];

  const serviceAreas = [
    'New Braunfels', 'Seguin', 'San Marcos', 
    'Canyon Lake', 'Spring Branch', 'Schertz', 'Garden Ridge', 'Gruene'
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden" data-testid="hero-section">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1768076955999-a94564017866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBob3VzZSUyMHBhaW50ZXIlMjBpbnRlcmlvciUyMGV4dGVyaW9yfGVufDB8fHx8MTc3MDQwNDAwNHww&ixlib=rb-4.1.0&q=85)'
          }}
        />
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/70 to-slate-950" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-3xl">
            {/* Trust badge with glowing border */}
            <div className="relative inline-block mb-6 group">
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-amber-400 to-cyan-400 rounded-sm blur-lg opacity-70" />
              <div className="relative inline-flex items-center gap-2 bg-slate-950/90 border-2 border-amber-400/80 px-5 py-3 shadow-[0_0_20px_rgba(245,158,11,0.5)]">
                <Star className="w-5 h-5 text-amber-400" />
                <span className="text-amber-400 text-base font-bold uppercase tracking-wider">25+ Years Experience</span>
              </div>
            </div>
            
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-7xl text-white uppercase leading-tight mb-6">
              Residential Painting
              <span className="block text-amber-500">Done Right</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-2xl">
              Transform your home with professional painting services backed by over two decades of expertise. 
              Detailed written proposals, quality craftsmanship, and results that last.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="glow-button bg-amber-500 text-black font-bold uppercase tracking-wider px-8 py-4 text-center skew-button hover:bg-amber-400 transition-all"
                data-testid="hero-cta-estimate"
              >
                <span>Get a Free Estimate</span>
              </Link>
              <Link
                to="/free-strategy-call"
                className="relative group inline-block"
                data-testid="hero-cta-call"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-amber-400 to-cyan-400 rounded-sm blur-lg opacity-70 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative border-2 border-cyan-400/80 bg-slate-950/90 text-white font-bold uppercase tracking-wider px-8 py-4 text-center group-hover:border-amber-400 group-hover:text-amber-400 transition-all shadow-[0_0_20px_rgba(6,182,212,0.5)]">
                  Book Free Strategy Call
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 sm:py-28 bg-slate-950" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white uppercase mb-4">
              Our <span className="text-amber-500">Services</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Comprehensive residential painting solutions tailored to your home. We also offer commercial services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <GlowingCard key={service.id} className={service.featured ? 'md:col-span-2 lg:col-span-1' : ''}>
                <div className="p-6 sm:p-8">
                  <div className="w-14 h-14 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-amber-500" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white uppercase mb-3">{service.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{service.description}</p>
                </div>
              </GlowingCard>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="about" className="py-20 sm:py-28 bg-slate-900" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white uppercase mb-6">
                Why Choose <span className="text-amber-500">Pro Painting?</span>
              </h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                With over 25 years of experience in residential painting, we bring unmatched expertise 
                and dedication to every project. Our commitment to quality and customer satisfaction 
                sets us apart.
              </p>

              <ul className="space-y-4">
                {[
                  '25+ Years of Professional Experience',
                  'Residential-Focused Expertise',
                  'Detailed Written Proposals',
                  'Quality Materials & Craftsmanship',
                  'Clean, Respectful Work Environment',
                  'Satisfaction Guaranteed'
                ].map((item) => (
                  <li key={item.replace(/\s+/g, '-').toLowerCase()} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-bold uppercase tracking-wider mt-8 transition-colors"
              >
                Get Started Today <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/20 via-cyan-500/20 to-amber-500/20 blur-2xl opacity-50" />
              <img
                src="https://images.unsplash.com/photo-1758972581344-85dd3ccb10db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwZGFyayUyMHdhbGwlMjBwYWludHxlbnwwfHx8fDE3NzA0MDQwMDh8MA&ixlib=rb-4.1.0&q=85"
                alt="Modern living room with professionally painted walls"
                className="relative w-full aspect-[4/3] object-cover border border-slate-800"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 sm:py-28 bg-slate-950" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white uppercase mb-4">
              Simple <span className="text-amber-500">Process</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              From first contact to project completion, we make the process seamless.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <div key={step.id} className="relative">
                {step.link ? (
                  <Link to={step.link}>
                    <GlowingCard>
                      <div className="p-6 text-center cursor-pointer hover:bg-slate-800/50 transition-colors">
                        <div className="w-16 h-16 mx-auto bg-slate-950 border border-amber-500/30 flex items-center justify-center mb-4">
                          <step.icon className="w-8 h-8 text-amber-500" />
                        </div>
                        <div className="absolute -top-3 -left-3 w-8 h-8 bg-amber-500 flex items-center justify-center">
                          <span className="font-heading font-black text-black">{index + 1}</span>
                        </div>
                        <h3 className="font-heading font-bold text-lg text-white uppercase mb-2">{step.title}</h3>
                        <p className="text-slate-400 text-sm">{step.description}</p>
                        <span className="text-amber-500 text-xs uppercase tracking-wider mt-3 inline-block">Click Here →</span>
                      </div>
                    </GlowingCard>
                  </Link>
                ) : (
                  <GlowingCard>
                    <div className="p-6 text-center">
                      <div className="w-16 h-16 mx-auto bg-slate-950 border border-amber-500/30 flex items-center justify-center mb-4">
                        <step.icon className="w-8 h-8 text-amber-500" />
                      </div>
                      <div className="absolute -top-3 -left-3 w-8 h-8 bg-amber-500 flex items-center justify-center">
                        <span className="font-heading font-black text-black">{index + 1}</span>
                      </div>
                      <h3 className="font-heading font-bold text-lg text-white uppercase mb-2">{step.title}</h3>
                      <p className="text-slate-400 text-sm">{step.description}</p>
                    </div>
                  </GlowingCard>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 sm:py-28 bg-slate-900" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white uppercase mb-4">
              What Our <span className="text-amber-500">Clients Say</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Real Review */}
            <GlowingCard>
              <div className="p-6 sm:p-8">
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={`review1-star-${star}`} className="w-5 h-5 text-amber-500 fill-amber-500" />
                  ))}
                </div>
                <p className="text-slate-400 italic mb-6">
                  "Pro Painting did an excellent job on our interior and exterior painting after a devastating roof leak. Matt also redesigned and resurfaced a kitchen island for me that turned out gorgeous. Very neat and thorough, I trusted them completely around my home and family. Highly recommend Pro Painting!"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center">
                    <span className="font-heading font-bold text-amber-500">VH</span>
                  </div>
                  <div>
                    <p className="font-bold text-white">Verified Homeowner</p>
                    <p className="text-slate-500 text-sm">New Braunfels, TX</p>
                  </div>
                </div>
              </div>
            </GlowingCard>

            {/* Testimonial 2 */}
            <GlowingCard>
              <div className="p-6 sm:p-8">
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={`review2-star-${star}`} className="w-5 h-5 text-amber-500 fill-amber-500" />
                  ))}
                </div>
                <p className="text-slate-400 italic mb-6">
                  "Matt and his team were professional from start to finish. The detailed written proposal made it easy to understand the scope of work. Our home looks brand new after their exterior painting job. Highly recommend!"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center">
                    <span className="font-heading font-bold text-amber-500">SM</span>
                  </div>
                  <div>
                    <p className="font-bold text-white">Sarah M.</p>
                    <p className="text-slate-500 text-sm">Canyon Lake, TX</p>
                  </div>
                </div>
              </div>
            </GlowingCard>

            {/* Testimonial 3 */}
            <GlowingCard>
              <div className="p-6 sm:p-8">
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={`review3-star-${star}`} className="w-5 h-5 text-amber-500 fill-amber-500" />
                  ))}
                </div>
                <p className="text-slate-400 italic mb-6">
                  "25 years of experience really shows in their work. The attention to detail on our trim and cabinet refinishing was exceptional. Clean, respectful, and on schedule. We'll definitely use Pro Painting again."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center">
                    <span className="font-heading font-bold text-amber-500">RK</span>
                  </div>
                  <div>
                    <p className="font-bold text-white">Robert K.</p>
                    <p className="text-slate-500 text-sm">Seguin, TX</p>
                  </div>
                </div>
              </div>
            </GlowingCard>
          </div>
        </div>
      </section>

      {/* Gallery Placeholder */}
      <section className="py-20 sm:py-28 bg-slate-950" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white uppercase mb-4">
              Our <span className="text-amber-500">Work</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Before and after transformations showcasing our quality craftsmanship.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { id: 'gallery-1', src: 'https://images.unsplash.com/photo-1758972581344-85dd3ccb10db?w=400&h=300&fit=crop' },
              { id: 'gallery-2', src: 'https://images.unsplash.com/photo-1767112316630-b6c54f726af6?w=400&h=300&fit=crop' },
              { id: 'gallery-3', src: 'https://images.unsplash.com/photo-1605972082877-46c5f53df51d?w=400&h=300&fit=crop' },
              { id: 'gallery-4', src: 'https://images.unsplash.com/photo-1738837546028-2ef9ffc04658?w=400&h=300&fit=crop' },
            ].map((img, index) => (
              <div key={img.id} className="relative aspect-square overflow-hidden group">
                <img
                  src={img.src}
                  alt={`Project ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-amber-500/0 group-hover:bg-amber-500/20 transition-colors duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 sm:py-28 bg-slate-900" data-testid="service-areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white uppercase mb-4">
              Service <span className="text-amber-500">Areas</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Proudly serving New Braunfels and the surrounding Texas Hill Country communities.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {serviceAreas.map((area) => (
              <div
                key={area.replace(/\s+/g, '-').toLowerCase()}
                className="flex items-center gap-2 bg-slate-950 border border-slate-800 px-6 py-3 hover:border-amber-500/50 transition-colors"
              >
                <MapPin className="w-4 h-4 text-amber-500" />
                <span className="text-slate-300 font-medium">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28 bg-slate-950 relative overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-cyan-500/5 to-amber-500/5" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white uppercase mb-6">
            Ready to Transform <span className="text-amber-500">Your Home?</span>
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Get started with a free estimate or book a strategy call to discuss your project. 
            We're here to bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="glow-button bg-amber-500 text-black font-bold uppercase tracking-wider px-8 py-4 skew-button hover:bg-amber-400 transition-all"
              data-testid="final-cta-estimate"
            >
              <span>Get a Free Estimate</span>
            </Link>
            <Link
              to="/free-strategy-call"
              className="relative group inline-block"
              data-testid="final-cta-call"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-amber-400 to-cyan-400 rounded-sm blur-lg opacity-70 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative border-2 border-cyan-400/80 bg-slate-950/90 text-white font-bold uppercase tracking-wider px-8 py-4 text-center group-hover:border-amber-400 group-hover:text-amber-400 transition-all shadow-[0_0_20px_rgba(6,182,212,0.5)]">
                Book Free Strategy Call
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
